import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Trophy, Brain, Play } from 'lucide-react';
import { auth } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useGameStore from '../lib/gameStore';
import PasswordInput from '../components/PasswordInput';
import Footer from '../components/Footer';
import { registerSchema, loginSchema, forgotPasswordSchema, validateWithZod } from '../utils/validation';

const Landing = () => {
  const navigate = useNavigate();
  const { setUser } = useGameStore();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { session } = await auth.getSession();
      if (session) {
        navigate('/dashboard', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const validateForm = () => {
    setValidationErrors({});

    const schema = isLogin ? loginSchema : registerSchema;
    const dataToValidate = isLogin
      ? { email: formData.email, password: formData.password }
      : formData;

    const result = validateWithZod(schema, dataToValidate);

    if (!result.success) {
      setValidationErrors(result.errors);
      // Mostrar el primer error como toast
      const firstError = Object.values(result.errors)[0];
      if (firstError) {
        toast.error(firstError);
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { data, error } = isLogin
        ? await auth.signIn(formData.email, formData.password)
        : await auth.signUp(formData.email, formData.password, formData.name);

      if (error) {
        throw error;
      }

      if (!isLogin) {
        toast.success('¡Cuenta creada! Por favor verifica tu email antes de iniciar sesión.');
        setIsLogin(true);
        setFormData({ email: '', password: '', confirmPassword: '', name: '' });
      } else {
        const userId = data.user.id;
        setUser({ id: userId });
        toast.success('¡Bienvenido de nuevo!');
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = error.message || 'Error en la autenticación';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    const result = validateWithZod(forgotPasswordSchema, { email: forgotEmail });

    if (!result.success) {
      const firstError = Object.values(result.errors)[0];
      if (firstError) {
        toast.error(firstError);
      }
      return;
    }

    setLoading(true);
    try {
      const { error } = await auth.resetPassword(forgotEmail);

      if (error) {
        throw error;
      }

      toast.success('¡Email enviado! Revisa tu bandeja de entrada');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      const errorMessage = error.message || 'Error al enviar el email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Limpiar error del campo cuando el usuario empieza a escribir
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: undefined });
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden relative flex flex-col">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse-glow" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 flex-1">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex flex-col items-center justify-center gap-6 mb-6">
            {/* Logo Image */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mb-4"
            >
              <img
                src="/logo.png"
                alt="n8n play logo"
                className="h-40 md:h-48 object-contain mx-auto"
              />
            </motion.div>
          </div>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Aprende automatización de forma divertida y adictiva. Construye flujos, desbloquea logros y conviértete en un maestro de n8n.
          </p>
        </motion.div>

        {/* Features */}
        {!showAuth && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            <FeatureCard
              icon={<Brain className="w-12 h-12" />}
              title="Aprende Haciendo"
              description="Construye flujos reales mientras aprendes conceptos de automatización"
              delay={0.4}
            />
            <FeatureCard
              icon={<Trophy className="w-12 h-12" />}
              title="Desbloquea Logros"
              description="Gana XP, badges y sube de nivel mientras dominas n8n"
              delay={0.5}
            />
            <FeatureCard
              icon={<Zap className="w-12 h-12" />}
              title="Progreso Visible"
              description="Sigue tu aprendizaje con niveles progresivos y feedback instantáneo"
              delay={0.6}
            />
          </motion.div>
        )}

        {/* CTA Button or Auth Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
          className="flex justify-center"
        >
          {!showAuth ? (
            <button
              onClick={() => setShowAuth(true)}
              className="btn-neon px-12 py-4 rounded-button text-xl font-heading font-bold flex items-center gap-3"
              data-testid="get-started-btn"
            >
              <Play className="w-6 h-6" fill="currentColor" />
              Comenzar a Aprender
            </button>
          ) : (
            <div className="glass p-8 rounded-card max-w-md w-full" data-testid="auth-form">
              <h2 className="text-2xl font-heading font-bold text-text-primary mb-6 text-center">
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Nombre</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required={!isLogin}
                      className={`w-full bg-background-subtle border ${
                        validationErrors.name ? 'border-red-500' : 'border-slate-700'
                      } focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-lg h-10 px-3`}
                      data-testid="name-input"
                    />
                    {validationErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full bg-background-subtle border ${
                      validationErrors.email ? 'border-red-500' : 'border-slate-700'
                    } focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-lg h-10 px-3`}
                    data-testid="email-input"
                  />
                  {validationErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Contraseña</label>
                  <PasswordInput
                    value={formData.password}
                    onChange={handleChange}
                    name="password"
                    required
                    placeholder="Mínimo 8 caracteres"
                    testId="password-input"
                    className={validationErrors.password ? 'border-red-500' : ''}
                  />
                  {validationErrors.password && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
                  )}
                </div>
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Confirmar Contraseña</label>
                    <PasswordInput
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      name="confirmPassword"
                      required
                      placeholder="Repite la contraseña"
                      testId="confirm-password-input"
                      className={validationErrors.confirmPassword ? 'border-red-500' : ''}
                    />
                    {validationErrors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.confirmPassword}</p>
                    )}
                  </div>
                )}
                {isLogin && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:text-primary-hover transition-colors"
                      data-testid="forgot-password-btn"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-neon py-3 rounded-button font-heading font-bold"
                  data-testid="submit-btn"
                >
                  {loading ? 'Cargando...' : isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
                </button>
              </form>
              <div className="mt-4 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary hover:text-primary-hover transition-colors"
                  data-testid="toggle-auth-btn"
                >
                  {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowForgotPassword(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="glass p-8 rounded-card max-w-md w-full"
            data-testid="forgot-password-modal"
          >
            <h2 className="text-2xl font-heading font-bold text-text-primary mb-4">
              Recuperar Contraseña
            </h2>
            <p className="text-text-secondary mb-6">
              Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Email</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="tu@email.com"
                  className="w-full bg-background-subtle border border-slate-700 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-lg h-10 px-3"
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 glass py-3 rounded-lg hover:border-primary/50 transition-all"
                  data-testid="cancel-forgot-btn"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-neon py-3 rounded-lg font-heading font-bold disabled:opacity-50"
                  data-testid="submit-forgot-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ scale: 1.05, y: -5 }}
    className="glass p-6 rounded-card text-center group hover:border-primary/50 transition-all"
  >
    <div className="flex justify-center mb-4 text-primary group-hover:scale-110 transition-transform">{icon}</div>
    <h3 className="text-xl font-heading font-bold text-text-primary mb-2">{title}</h3>
    <p className="text-text-secondary">{description}</p>
  </motion.div>
);

export default Landing;